import { useState, useEffect, useCallback } from 'react';

const MAX_SNAPSHOTS = 30;

const useSnapshotHistory = (appId, currentState) => {
    const storageKey = `${appId}_history`;
    const [history, setHistory] = useState([]);

    // Load history from local storage on mount
    useEffect(() => {
        try {
            const stored = localStorage.getItem(storageKey);
            if (stored) {
                setHistory(JSON.parse(stored));
            }
        } catch (e) {
            console.error("Failed to load history", e);
        }
    }, [storageKey]);

    // Save history to local storage whenever it changes
    useEffect(() => {
        if (history.length > 0) {
            localStorage.setItem(storageKey, JSON.stringify(history));
        } else {
            // Optional: clear key if empty? Or just keep empty array.
            // Keeping it simple.
            localStorage.setItem(storageKey, JSON.stringify([]));
        }
    }, [history, storageKey]);

    const saveSnapshot = useCallback((label) => {
        if (!currentState) return;

        const newSnapshot = {
            id: crypto.randomUUID(),
            timestamp: Date.now(),
            label: label || 'Manual Save',
            data: currentState
        };

        setHistory(prev => {
            const updated = [newSnapshot, ...prev];
            if (updated.length > MAX_SNAPSHOTS) {
                return updated.slice(0, MAX_SNAPSHOTS);
            }
            return updated;
        });
    }, [currentState]);

    const restoreSnapshot = useCallback((id) => {
        const snapshot = history.find(s => s.id === id);
        return snapshot ? snapshot.data : null;
    }, [history]);

    const deleteSnapshot = useCallback((id) => {
        setHistory(prev => prev.filter(s => s.id !== id));
    }, []);

    const clearHistory = useCallback(() => {
        setHistory([]);
    }, []);

    return {
        history,
        saveSnapshot,
        restoreSnapshot,
        deleteSnapshot,
        clearHistory
    };
};

export default useSnapshotHistory;
